import React, { useState, useEffect, useCallback } from 'react';
import api, { uploadMedia } from '../utils/api';
import ImageCropperModal from './ImageCropperModal';
import './EditServiceStaffDialog.css';

const EditServiceStaffDialog = ({ staff, subunits, onClose, onEdit }) => {
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    email: '',
    designation: '',
    phone: '',
    dept_ref: '',
    service_ref: '',
    dp: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [services, setServices] = useState([]);
  const [availableSubunits, setAvailableSubunits] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      if (response.data.success) {
        setServices(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch services:', err);
    }
  };

  const fetchSubunitsForService = useCallback(async (serviceId) => {
    if (!serviceId) return;
    try {
      const response = await api.get(`/service-subunits?administration_ref=${serviceId}`);
      if (response.data.success) {
        setAvailableSubunits(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch subunits for service:', err);
    }
  }, []);

  useEffect(() => {
    if (staff) {
      const serviceRefId = staff.service_ref?._id || staff.service_ref || '';
      const deptRefId = staff.dept_ref?._id || staff.dept_ref || '';
      setFormData({
        name: staff.name || '',
        nameEn: staff.nameEn || '',
        email: staff.email || '',
        designation: staff.designation || '',
        phone: staff.phone || '',
        dept_ref: deptRefId,
        service_ref: serviceRefId,
        dp: staff.dp || ''
      });
      fetchServices();
      if (serviceRefId) {
        fetchSubunitsForService(serviceRefId);
      }
    }
  }, [staff, fetchSubunitsForService]);

  useEffect(() => {
    if (formData.service_ref) {
      fetchSubunitsForService(formData.service_ref);
    }
  }, [formData.service_ref, fetchSubunitsForService]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file.');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB.');
        return;
      }
      
      setSelectedFile(file);
      setError('');
      // Open cropper modal
      setShowCropper(true);
    }
  };

  const handleCropComplete = (croppedBlob) => {
    setShowCropper(false);
    
    // Create a File object from the blob
    const croppedFile = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
    setSelectedFile(croppedFile);
    
    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(prev => ({
        ...prev,
        dp: e.target.result
      }));
    };
    reader.readAsDataURL(croppedBlob);
  };

  const handleRemoveDP = () => {
    setFormData(prev => ({
      ...prev,
      dp: ''
    }));
    setSelectedFile(null);
    const fileInput = document.getElementById('dp-upload-service-staff-edit');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    if (!formData.designation.trim()) {
      setError('Designation is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Upload DP if selected
      let dpUrl = formData.dp; // Keep existing URL if no new file
      
      if (selectedFile) {
        const uploadResult = await uploadMedia(selectedFile);
        if (!uploadResult.success) {
          setError(uploadResult.error || 'Failed to upload image');
          setLoading(false);
          return;
        }
        dpUrl = uploadResult.url;
      }

      // Prepare data with uploaded DP URL
      const submitData = {
        ...formData,
        dp: dpUrl || null
      };

      await onEdit(staff._id, submitData);
      
      // Clear selected file after successful submission
      setSelectedFile(null);
      
      // Close dialog on success
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!staff) return null;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>Edit Service Staff Member</h3>
          <button 
            className="close-button" 
            onClick={handleClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter full name"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="nameEn">English Name</label>
            <input
              type="text"
              id="nameEn"
              name="nameEn"
              value={formData.nameEn}
              onChange={handleInputChange}
              placeholder="Enter English name"
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter email address"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="designation">Designation *</label>
            <input
              type="text"
              id="designation"
              name="designation"
              value={formData.designation}
              onChange={handleInputChange}
              placeholder="Enter designation"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Enter phone number"
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="service_ref">Service Unit *</label>
            <select
              id="service_ref"
              name="service_ref"
              value={formData.service_ref}
              onChange={handleInputChange}
              required
              disabled={loading}
              className="form-input"
            >
              <option value="">Select a service unit</option>
              {services.map((service) => (
                <option key={service._id} value={service._id}>
                  {service.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="dept_ref">Subunit *</label>
            <select
              id="dept_ref"
              name="dept_ref"
              value={formData.dept_ref}
              onChange={handleInputChange}
              required
              disabled={loading || !formData.service_ref}
              className="form-input"
            >
              <option value="">Select a subunit</option>
              {availableSubunits.map((subunit) => (
                <option key={subunit._id} value={subunit._id}>
                  {subunit.name} ({subunit.nameEn})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="dp-upload-service-staff-edit">Display Picture</label>
            <div className="avatar-upload-container">
              <div 
                className={`circular-avatar ${selectedFile || formData.dp ? 'has-content' : ''}`}
                onClick={() => !loading && document.getElementById('dp-upload-service-staff-edit').click()}
              >
                {formData.dp ? (
                  <img src={formData.dp} alt="Profile" className="avatar-image" loading="lazy" decoding="async" />
                ) : selectedFile ? (
                  <img 
                    src={URL.createObjectURL(selectedFile)} 
                    alt="Profile" 
                    className="avatar-image preview" 
                  />
                ) : (
                  <div className="avatar-placeholder">
                    <span className="avatar-icon">📷</span>
                    <span className="avatar-text">Click to upload</span>
                  </div>
                )}
              </div>
              
              <input
                type="file"
                id="dp-upload-service-staff-edit"
                accept="image/*"
                onChange={handleFileSelect}
                disabled={loading}
                className="file-input-hidden"
                style={{ display: 'none' }}
              />
              
              {(selectedFile || formData.dp) && (
                <div className="avatar-actions">
                  {selectedFile && (
                    <span className="upload-info">
                      Will be uploaded when you save
                    </span>
                  )}
                  {formData.dp && (
                    <button 
                      type="button" 
                      onClick={handleRemoveDP}
                      className="remove-button"
                      disabled={loading}
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="dialog-actions">
            <button
              type="button"
              onClick={handleClose}
              className="cancel-button"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={loading || !formData.name.trim() || !formData.email.trim() || !formData.designation.trim() || !formData.service_ref || !formData.dept_ref}
            >
              {loading ? 'Updating...' : 'Update Staff'}
            </button>
          </div>
        </form>

        {showCropper && selectedFile && (
          <ImageCropperModal
            imageFile={selectedFile}
            onCropComplete={handleCropComplete}
            onCancel={() => {
              setShowCropper(false);
              setSelectedFile(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default EditServiceStaffDialog;
